import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Minus, Plus } from '@gravity-ui/icons';
import { getFrameGanttV3 } from '../../services/statistics';
import { useNavigationEntitlements } from '../../hooks/useNavigationEntitlements';
import { isNavigationKeyAllowed, NAV_KEYS } from '../../utils/navigationEntitlements';
import { formatAxisTick, formatInTimezone } from '../../utils/formatInTimezone';
import { countRu, FRAME_FORMS } from '../../utils/pluralRu';
import {
  buildFrameGanttParams,
  deriveMetricState,
  describeEvent,
  describeFrameGanttError,
  describeMetricState,
  EVENT_CATEGORIES,
  findPointIndex,
  FRAME_GANTT_PAGE_SIZE,
  isMetricDisabled,
  layoutEventLanes,
  mergeFrameGanttPages,
  normalizeFrameGanttResponse,
  resolveGanttWindow,
  STATE_TONES,
} from './frameGanttModel';
import './FrameGantt.css';

const ROW_HEIGHT = 34;
const EVENT_ROW_HEIGHT = 20;
const AXIS_HEIGHT = 26;
const POINT_STEP = 14;
const POINT_HEIGHT = 22;
const POINT_MAX_WIDTH = 10;
const MIN_WIDTH = 640;
const ZOOM_LEVELS = [0.5, 1, 2, 4];
const DRAG_THRESHOLD = 5;
const TICK_SPACING = 140;
// За сколько пикселей до правого края шкалы подгружать следующие кадры.
const LOAD_MORE_EDGE = 200;

const EVENT_NAV_KEYS = { business: NAV_KEYS.SUMMARY_BUSINESS_METRICS };

const readStoredList = (key) => {
  try {
    const parsed = JSON.parse(localStorage.getItem(key));
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const writeStoredList = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Настройка — удобство, без хранилища работает выбор по умолчанию.
  }
};

/**
 * ТЗ V5 №16: многодорожечный Гант кадров камеры над таблицей кадров.
 * Фильтры — общие с таблицей (приходят пропсом). Каждая выбранная метрика —
 * своя дорожка, одна точка — один кадр; ниже дорожки событий. Клик по точке
 * выбирает именно этот frame_id; перетаскивание шкалы клик не вызывает.
 */
const FrameGantt = ({
  cameraId,
  filters,
  metricOptions,
  timezone,
  selectedFrameId,
  onSelectFrame,
  storageKey = 'frameGantt',
  disabledMetrics,
}) => {
  const navigate = useNavigate();
  const { keys: navigationKeys } = useNavigationEntitlements();

  const allowedCategories = useMemo(
    () => EVENT_CATEGORIES.filter((category) => !EVENT_NAV_KEYS[category.id]
      || isNavigationKeyAllowed(navigationKeys, EVENT_NAV_KEYS[category.id])),
    [navigationKeys]
  );

  const metricsKey = `${storageKey}_metrics`;
  const [selectedMetrics, setSelectedMetrics] = useState(() => {
    const stored = readStoredList(metricsKey);
    const known = new Set(metricOptions.map((metric) => metric.key));
    const restored = stored?.filter((key) => known.has(key));
    return restored?.length ? restored : metricOptions.filter((metric) => !metric.defaultHidden).map((metric) => metric.key);
  });
  const [selectedCategories, setSelectedCategories] = useState(() => EVENT_CATEGORIES.map((category) => category.id));
  const [zoomIndex, setZoomIndex] = useState(1);

  const metrics = useMemo(
    () => metricOptions.filter((metric) => selectedMetrics.includes(metric.key)),
    [metricOptions, selectedMetrics]
  );
  // Запрещённая правами категория не запрашивается и не показывается.
  const eventTypes = useMemo(
    () => allowedCategories.map((category) => category.id).filter((id) => selectedCategories.includes(id)),
    [allowedCategories, selectedCategories]
  );

  const filtersKey = JSON.stringify(filters || {});
  // Окно фиксируется при смене фильтра: листание страниц не сдвигает «последние сутки».
  const ganttWindow = useMemo(() => resolveGanttWindow(JSON.parse(filtersKey)), [filtersKey]);
  const requestKey = `${cameraId}|${filtersKey}|${metrics.map((m) => m.key).join(',')}|${eventTypes.join(',')}`;

  // Страницы копятся: прокрутка к правому краю шкалы подгружает следующую,
  // и шкала продолжается дальше, а не обрывается на первых 500 кадрах.
  const [result, setResult] = useState({
    key: '', status: 'idle', pages: [], error: '', more: 'idle', moreError: '',
  });
  const [retryTick, setRetryTick] = useState(0);
  const pendingSelectRef = useRef(null);
  const moreControllerRef = useRef(null);

  const loadKey = `${requestKey}|${retryTick}`;

  const fetchPage = useCallback((offset, signal) => getFrameGanttV3(cameraId, buildFrameGanttParams({
    filters: JSON.parse(filtersKey),
    window: ganttWindow,
    metrics: metrics.map((metric) => metric.key),
    eventTypes,
    offset,
    limit: FRAME_GANTT_PAGE_SIZE,
  }), { signal }).then(normalizeFrameGanttResponse),
  // loadKey включает всё, от чего зависит запрос.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [loadKey]);

  useEffect(() => {
    if (cameraId == null) return undefined;
    const controller = new AbortController();
    moreControllerRef.current?.abort();
    pendingSelectRef.current = null;
    setResult({ key: loadKey, status: 'loading', pages: [], error: '', more: 'idle', moreError: '' });
    fetchPage(0, controller.signal)
      .then((page) => {
        if (controller.signal.aborted) return;
        setResult({ key: loadKey, status: 'ready', pages: [page], error: '', more: 'idle', moreError: '' });
      })
      .catch((error) => {
        if (controller.signal.aborted || error?.name === 'CanceledError') return;
        // Старые точки при ошибке не остаются: страницы сбрасываются.
        setResult({ key: loadKey, status: 'error', pages: [], error: describeFrameGanttError(error), more: 'idle', moreError: '' });
      });
    return () => controller.abort();
  }, [cameraId, loadKey, fetchPage]);

  useEffect(() => () => moreControllerRef.current?.abort(), []);

  const current = result.key === loadKey ? result : { status: 'loading', pages: [], error: '', more: 'idle' };
  const data = useMemo(() => mergeFrameGanttPages(current.pages), [current.pages]);
  const points = useMemo(() => data?.points || [], [data]);
  const displayTimezone = data?.timezone || timezone;
  const total = data?.total ?? null;
  const loadingMore = current.more === 'loading';

  const loadMore = useCallback(() => {
    if (result.key !== loadKey || result.status !== 'ready' || result.more === 'loading') return;
    const last = result.pages[result.pages.length - 1];
    if (!last?.hasMore) return;
    const controller = new AbortController();
    moreControllerRef.current = controller;
    setResult((prev) => (prev.key === loadKey ? { ...prev, more: 'loading', moreError: '' } : prev));
    fetchPage(last.nextOffset, controller.signal)
      .then((page) => {
        if (controller.signal.aborted) return;
        setResult((prev) => (prev.key === loadKey
          ? { ...prev, pages: [...prev.pages, page], more: 'idle' }
          : prev));
      })
      .catch((error) => {
        if (controller.signal.aborted || error?.name === 'CanceledError') return;
        pendingSelectRef.current = null;
        setResult((prev) => (prev.key === loadKey
          ? { ...prev, more: 'error', moreError: describeFrameGanttError(error) }
          : prev));
      });
  }, [result, loadKey, fetchPage]);

  // После подгрузки следующей страницы «Следующий кадр» выбирает её первый кадр.
  useEffect(() => {
    const pending = pendingSelectRef.current;
    if (pending == null || points.length <= pending) return;
    pendingSelectRef.current = null;
    onSelectFrame?.(points[pending].frame_id);
  }, [points, onSelectFrame]);

  const selectedIndex = findPointIndex(points, selectedFrameId);

  const step = useCallback((direction) => {
    if (current.status !== 'ready') return;
    const base = selectedIndex === -1 ? (direction > 0 ? -1 : points.length) : selectedIndex;
    const nextIndex = base + direction;
    if (nextIndex >= 0 && nextIndex < points.length) {
      onSelectFrame?.(points[nextIndex].frame_id);
      return;
    }
    if (direction > 0 && data?.hasMore) {
      pendingSelectRef.current = points.length;
      loadMore();
    }
  }, [current.status, selectedIndex, points, data, onSelectFrame, loadMore]);

  // ── Шкала ──────────────────────────────────────────────────────────────────
  const zoom = ZOOM_LEVELS[zoomIndex];
  const width = Math.max(MIN_WIDTH, Math.round(points.length * POINT_STEP * zoom));
  const scale = useMemo(() => {
    if (points.length === 0) return null;
    let t0 = points[0].atMs;
    let t1 = points[points.length - 1].atMs;
    if (t1 - t0 < 60000) { t0 -= 30000; t1 += 30000; }
    const pad = 6;
    return {
      t0, t1,
      x: (ms) => pad + ((ms - t0) / (t1 - t0)) * (width - pad * 2),
    };
  }, [points, width]);

  const eventLayouts = useMemo(() => {
    const events = data?.events || [];
    return allowedCategories
      .filter((category) => eventTypes.includes(category.id))
      .map((category) => ({
        category,
        ...layoutEventLanes(events.filter((event) => event.category === category.id)),
      }));
  }, [data, allowedCategories, eventTypes]);

  const ticks = useMemo(() => {
    if (!scale) return [];
    const count = Math.max(2, Math.floor(width / TICK_SPACING));
    const spanDays = (scale.t1 - scale.t0) / 86400000;
    return Array.from({ length: count + 1 }, (_, i) => {
      const ms = scale.t0 + ((scale.t1 - scale.t0) * i) / count;
      return { ms, label: formatAxisTick(new Date(ms).toISOString(), displayTimezone, spanDays >= 1) };
    });
  }, [scale, width, displayTimezone]);

  // ── Перетаскивание без случайного клика ────────────────────────────────────
  const scrollRef = useRef(null);
  const dragRef = useRef({ active: false, moved: false, x: 0, left: 0 });

  const handlePointerDown = (event) => {
    if (event.button != null && event.button !== 0) return;
    dragRef.current = { active: true, moved: false, x: event.clientX, left: scrollRef.current?.scrollLeft || 0 };
  };
  const handlePointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag.active) return;
    const dx = event.clientX - drag.x;
    if (!drag.moved && Math.abs(dx) < DRAG_THRESHOLD) return;
    drag.moved = true;
    if (scrollRef.current) scrollRef.current.scrollLeft = drag.left - dx;
  };
  const handlePointerUp = () => { dragRef.current.active = false; };

  // Подгрузка у правого края: и при прокрутке, и когда шкала уже целиком
  // помещается в окно (прокручивать ещё нечего, а кадры дальше есть).
  const maybeLoadMore = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !el.clientWidth || !data?.hasMore || current.more !== 'idle') return;
    if (el.scrollLeft + el.clientWidth >= el.scrollWidth - LOAD_MORE_EDGE) loadMore();
  }, [data, current.more, loadMore]);

  useEffect(() => { maybeLoadMore(); }, [points.length, zoomIndex, maybeLoadMore]);

  const handleClick = (event) => {
    if (dragRef.current.moved) {
      dragRef.current.moved = false;
      return;
    }
    const target = event.target.closest?.('[data-frame-id],[data-event-id]');
    if (!target) return;
    if (target.dataset.frameId) {
      const point = points.find((p) => String(p.frame_id) === target.dataset.frameId);
      if (point) onSelectFrame?.(point.frame_id);
      return;
    }
    const { eventId, eventCategory } = target.dataset;
    navigate(
      eventCategory === 'business' ? `/violations/${eventId}?type=business` : `/violations/${eventId}`,
      { state: { origin: 'gantt' } }
    );
  };

  const handleKeyDown = (event) => {
    if (event.key === 'ArrowLeft') { event.preventDefault(); step(-1); }
    if (event.key === 'ArrowRight') { event.preventDefault(); step(1); }
  };

  // Выбранный кадр держим в видимой части шкалы.
  const pointerX = scale && selectedIndex !== -1 ? scale.x(points[selectedIndex].atMs) : null;
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || pointerX == null || !el.clientWidth) return;
    if (pointerX < el.scrollLeft || pointerX > el.scrollLeft + el.clientWidth) {
      el.scrollLeft = Math.max(0, pointerX - el.clientWidth / 2);
    }
  }, [pointerX]);

  const toggleMetric = (key) => {
    setSelectedMetrics((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      writeStoredList(metricsKey, next);
      return next;
    });
  };
  const toggleCategory = (id) => {
    setSelectedCategories((prev) => (prev.includes(id) ? prev.filter((k) => k !== id) : [...prev, id]));
  };

  // ── Разметка строк ─────────────────────────────────────────────────────────
  // Ширина точки идёт за масштабом: на «мельче» соседние кадры не слипаются.
  const pointWidth = Math.max(3, Math.min(POINT_MAX_WIDTH, Math.round(POINT_STEP * zoom * 0.7)));
  const rows = [
    ...metrics.map((metric) => ({
      type: 'metric',
      key: metric.key,
      label: metric.label,
      metric,
      height: ROW_HEIGHT,
      disabled: isMetricDisabled(metric.key, data?.metricsMeta, disabledMetrics),
    })),
    ...eventLayouts.map((layout) => ({
      type: 'events',
      key: `events-${layout.category.id}`,
      label: layout.category.label,
      layout,
      height: Math.max(1, layout.laneCount) * EVENT_ROW_HEIGHT + 8,
    })),
  ];
  let y = AXIS_HEIGHT;
  const placedRows = rows.map((row) => {
    const placed = { ...row, y };
    y += row.height;
    return placed;
  });
  const svgHeight = y + 2;

  const renderMetricRow = (row) => {
    const cy = row.y + row.height / 2;
    if (row.disabled) {
      // Детектор выключен: значений нет не потому, что кадров нет.
      return (
        <g key={row.key} className="fg-row fg-row--disabled" data-testid={`frame-gantt-row-${row.key}`}>
          <rect x={0} y={row.y + 4} width={width} height={row.height - 8} fill="url(#fg-hatch)">
            <title>Метрика не включена для камеры</title>
          </rect>
          <line x1={0} x2={width} y1={row.y + row.height} y2={row.y + row.height} className="fg-row-line" />
        </g>
      );
    }
    return (
      <g key={row.key} className="fg-row" data-testid={`frame-gantt-row-${row.key}`}>
        <line x1={0} x2={width} y1={row.y + row.height} y2={row.y + row.height} className="fg-row-line" />
        {points.map((point) => {
          const derived = deriveMetricState(row.metric, point);
          const tone = STATE_TONES[derived.state] || 'unknown';
          const time = formatInTimezone(point.display_at || point.at, displayTimezone);
          return (
            <rect
              key={point.frame_id}
              data-frame-id={String(point.frame_id)}
              data-tone={tone}
              className={`fg-point fg-point--${tone}`}
              x={scale.x(point.atMs) - pointWidth / 2}
              y={cy - POINT_HEIGHT / 2}
              width={pointWidth}
              height={POINT_HEIGHT}
              rx={2}
            >
              <title>{`${time} · ${describeMetricState(row.metric, point, derived)}`}</title>
            </rect>
          );
        })}
      </g>
    );
  };

  const renderEventRow = (row) => {
    const { category, events } = row.layout;
    const clampX = (ms) => Math.min(width, Math.max(0, scale.x(ms)));
    return (
      <g key={row.key} className="fg-row" data-testid={`frame-gantt-row-${row.key}`}>
        <line x1={0} x2={width} y1={row.y + row.height} y2={row.y + row.height} className="fg-row-line" />
        {events.map((event) => {
          const x1 = clampX(event.fromMs);
          const x2 = clampX(event.endMs);
          if (x2 <= 0 || x1 >= width) return null;
          return (
            <rect
              key={`${event.event_id}-${event.fromMs}`}
              data-event-id={String(event.event_id)}
              data-event-category={category.id}
              className={`fg-event fg-event--${category.id}`}
              x={x1}
              y={row.y + 4 + event.lane * EVENT_ROW_HEIGHT}
              width={Math.max(3, x2 - x1)}
              height={EVENT_ROW_HEIGHT - 3}
              rx={2}
            >
              <title>{describeEvent(event, category, displayTimezone)}</title>
            </rect>
          );
        })}
      </g>
    );
  };

  const renderBody = () => {
    if (current.status === 'loading') return <div className="fg-message">Загрузка…</div>;
    if (current.status === 'error') {
      return (
        <div className="fg-message fg-message--error" role="alert">
          {current.error}
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setRetryTick((t) => t + 1)}>
            Повторить
          </button>
        </div>
      );
    }
    if (points.length === 0) return <div className="fg-message">За период кадров нет</div>;
    if (rows.length === 0) return <div className="fg-message">Выберите хотя бы одну дорожку</div>;
    return (
      <div className="fg-chart">
        <div className="fg-labels" style={{ paddingTop: AXIS_HEIGHT }}>
          {placedRows.map((row) => (
            <div
              key={row.key}
              className={`fg-label${row.disabled ? ' fg-label--disabled' : ''}`}
              style={{ height: row.height }}
              title={row.disabled ? `${row.label} · метрика не включена` : row.label}
            >
              <span className="fg-label-text">{row.label}</span>
              {row.disabled && <span className="fg-label-note">Не включена</span>}
            </div>
          ))}
        </div>
        <div
          ref={scrollRef}
          className="fg-scroll"
          tabIndex={0}
          role="group"
          aria-label="Диаграмма кадров. Стрелки влево и вправо переключают кадр."
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          onScroll={maybeLoadMore}
        >
          <svg width={width} height={svgHeight} className="fg-svg" data-testid="frame-gantt-svg">
            <defs>
              <pattern id="fg-hatch" width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                <rect width="8" height="8" className="fg-hatch-bg" />
                <line x1="0" y1="0" x2="0" y2="8" className="fg-hatch-line" />
              </pattern>
            </defs>
            {ticks.map((tick) => (
              <g key={tick.ms} className="fg-tick">
                <line x1={scale.x(tick.ms)} x2={scale.x(tick.ms)} y1={AXIS_HEIGHT - 4} y2={svgHeight} />
                <text x={scale.x(tick.ms)} y={AXIS_HEIGHT - 8} textAnchor="middle">{tick.label}</text>
              </g>
            ))}
            {placedRows.map((row) => (row.type === 'metric' ? renderMetricRow(row) : renderEventRow(row)))}
            {pointerX != null && (
              <g className="fg-pointer" data-testid="frame-gantt-pointer" pointerEvents="none">
                <line x1={pointerX} x2={pointerX} y1={AXIS_HEIGHT - 2} y2={svgHeight} />
              </g>
            )}
          </svg>
        </div>
      </div>
    );
  };

  const selectedPoint = selectedIndex !== -1 ? points[selectedIndex] : null;
  const canPrev = current.status === 'ready' && (selectedIndex > 0 || (selectedIndex === -1 && points.length > 0));
  const canNext = current.status === 'ready'
    && ((selectedIndex !== -1 && selectedIndex < points.length - 1) || (selectedIndex === -1 && points.length > 0) || Boolean(data?.hasMore));

  return (
    <div className="fg">
      <div className="fg-controls">
        <div className="fg-checks" role="group" aria-label="Дорожки метрик">
          {metricOptions.map((metric) => (
            <label key={metric.key} className="fg-check">
              <input type="checkbox" checked={selectedMetrics.includes(metric.key)} onChange={() => toggleMetric(metric.key)} />
              {metric.label}
            </label>
          ))}
        </div>
        {allowedCategories.length > 0 && (
          <div className="fg-checks" role="group" aria-label="Дорожки событий">
            {allowedCategories.map((category) => (
              <label key={category.id} className={`fg-check fg-check--${category.id}`}>
                <input type="checkbox" checked={selectedCategories.includes(category.id)} onChange={() => toggleCategory(category.id)} />
                {category.label}
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="fg-nav">
        <button type="button" className="fg-icon-btn" onClick={() => step(-1)} disabled={!canPrev}
          title="Предыдущий кадр" aria-label="Предыдущий кадр">
          <ChevronLeft width={16} height={16} />
        </button>
        <span className="fg-selected">
          {selectedPoint ? formatInTimezone(selectedPoint.display_at || selectedPoint.at, displayTimezone) : 'Кадр не выбран'}
        </span>
        <button type="button" className="fg-icon-btn" onClick={() => step(1)} disabled={!canNext}
          title="Следующий кадр" aria-label="Следующий кадр">
          <ChevronRight width={16} height={16} />
        </button>
        {current.status === 'ready' && points.length > 0 && (
          <span className="fg-range">
            {total != null && total > points.length
              ? `Загружено ${points.length} из ${countRu(total, FRAME_FORMS)}`
              : countRu(points.length, FRAME_FORMS)}
            {loadingMore ? ' · загрузка…' : ''}
          </span>
        )}
        <span className="fg-zoom">
          <button type="button" className="fg-icon-btn" onClick={() => setZoomIndex((i) => Math.max(0, i - 1))}
            disabled={zoomIndex === 0} title="Мельче" aria-label="Мельче">
            <Minus width={16} height={16} />
          </button>
          <button type="button" className="fg-icon-btn" onClick={() => setZoomIndex((i) => Math.min(ZOOM_LEVELS.length - 1, i + 1))}
            disabled={zoomIndex === ZOOM_LEVELS.length - 1} title="Крупнее" aria-label="Крупнее">
            <Plus width={16} height={16} />
          </button>
        </span>
      </div>

      {renderBody()}

      {current.status === 'ready' && current.more === 'error' && (
        <div className="fg-message fg-message--error" role="alert">
          {current.moreError}
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={loadMore}>
            Повторить
          </button>
        </div>
      )}

      {current.status === 'ready' && points.length > 0 && (
        <div className="fg-legend" aria-hidden="true">
          {metrics.some((m) => m.kind !== 'measured') && <span><i className="fg-swatch fg-point--ok" />Есть, совпадает</span>}
          {metrics.some((m) => m.kind === 'ppe') && <span><i className="fg-swatch fg-point--bad" />Не совпадает с числом людей</span>}
          {metrics.some((m) => m.kind !== 'measured') && <span><i className="fg-swatch fg-point--empty" />Людей нет</span>}
          <span><i className="fg-swatch fg-point--unknown" />Нет достоверных данных</span>
          {placedRows.some((row) => row.disabled) && <span><i className="fg-swatch fg-swatch--disabled" />Метрика не включена</span>}
          {metrics.some((m) => m.kind === 'measured') && <span><i className="fg-swatch fg-point--info" />Измерено</span>}
          {data?.hasMore && <span>Дальше ещё кадры, они подгрузятся при прокрутке вправо</span>}
        </div>
      )}
    </div>
  );
};

export default FrameGantt;
